import { Box, Stack } from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { portalPaths } from '@provision/portal-routes';
import { CreditCard } from 'lucide-react';
import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader } from 'src/components';
import { PageLayout } from 'src/components/Page';
import {
  PartnerGroupRules,
  usePartnerGroupRules,
  UserGroupRules,
  useUserGroupRuleValues,
} from 'src/graphql';
import { useGlobalStore } from 'src/store';
import { PartnerInvoicesSection } from './partner-invoices-section';
import { StripeConnectSection } from './stripe-connect-section';

const BillingPageShell = ({ children }: { children: ReactNode }) => (
  <V2ThemeProvider>
    <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
      <PageLayout
        title="Billing"
        subtitle="Manage your Stripe payout account and review invoices from OneVision."
        icon={<CreditCard />}
      >
        {children}
      </PageLayout>
    </Box>
  </V2ThemeProvider>
);

export const StripeSettingsView = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { partnerGroupRules, loading: partnerRulesLoading } =
    usePartnerGroupRules(selectedPartnerId);
  const { userGroupRules } = useUserGroupRuleValues();
  // Store starts as {}; treat that as still loading so invoice-only partners
  // are not hard-redirected before user rules hydrate.
  const userRulesHydrated = Object.keys(userGroupRules).length > 0;

  const hasAccessToSubscriptionManagement = Boolean(
    partnerGroupRules[PartnerGroupRules.AccessToSubscriptionManagement],
  );
  // Partner + user rules share the same id, so read each from its own source.
  // The merged groupRules map used by checkValidity cannot AND them.
  const hasAccessToViewPartnerInvoices =
    Boolean(partnerGroupRules[PartnerGroupRules.AccessToViewPartnerInvoices]) &&
    Boolean(userGroupRules[UserGroupRules.AccessToViewPartnerInvoices]);

  if (partnerRulesLoading || !userRulesHydrated) {
    return (
      <BillingPageShell>
        <Loader />
      </BillingPageShell>
    );
  }

  if (!hasAccessToSubscriptionManagement && !hasAccessToViewPartnerInvoices) {
    return <Navigate to={portalPaths.settings.companyInfo()} replace />;
  }

  return (
    <BillingPageShell>
      <Stack spacing={4}>
        {hasAccessToSubscriptionManagement ? <StripeConnectSection /> : null}
        {hasAccessToViewPartnerInvoices ? <PartnerInvoicesSection /> : null}
      </Stack>
    </BillingPageShell>
  );
};
